import { useCallback, useEffect, useMemo, useState } from 'react'
import Add from '@mui/icons-material/Add'
import CompareArrows from '@mui/icons-material/CompareArrows'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import Sync from '@mui/icons-material/Sync'
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { green } from '@mui/material/colors'
import useDocumentInvoiceData from '@/hooks/useDocumentInvoiceData'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { Document } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { PDPurchaseOrder, POLineItemMatch } from '@/types/po-match'
import {
  CellColumn,
  CellDiscrepancy,
  emphasizedCellKey,
} from '@/utils/po-discrepancies'
import { buildRelatedConnectorLines } from '@/utils/related-invoices'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import AddPurchaseOrderDialog from './AddPurchaseOrderDialog'
import ConnectorCanvas, { type MatchLineData } from './ConnectorCanvas'
import CreateMatchConfirmDialog from './CreateMatchConfirmDialog'
import DataGroupNode from './DataGroupNode'
import DiscrepancyPopover from './DiscrepancyPopover'
import LineItemsTable, { type LineItemColumnVisibility } from './LineItemsTable'
import PurchaseOrderData from './PurchaseOrderData'
import RelatedInvoicesList from './RelatedInvoicesList'
import {
  CONNECTOR_PANE_WIDTH,
  FieldItem,
  getSharedLineItemJob,
  getSharedLineItemPhase,
  INVOICE_PANE_WIDTH,
  isLumpSumPoLine,
  poNumbersMatch,
  PURCHASE_ORDER_PANE_WIDTH,
} from './shared'
import SkeletonPOCard from './SkeletonPOCard'
import UpdateVendorPopover from './UpdateVendorPopover'
import useConnectorPositions from './useConnectorPositions'
import useConnectorSelection from './useConnectorSelection'
import useMatchingPONumberPO from './useMatchingPONumberPO'
import usePODiscrepancies from './usePODiscrepancies'
import usePODisplayOrder from './usePODisplayOrder'
import usePOMatchData, { USER_MATCHED_REASON_ID } from './usePOMatchData'
import useRelatedInvoices from './useRelatedInvoices'

type PendingMatch = {
  invoiceRowId: string
  poLineItemId: string
  poId: string
}

type PurchaseOrderViewProps = {
  doc: Document
}

export default function PurchaseOrderView({ doc }: PurchaseOrderViewProps) {
  const { project } = useProjectContext()
  const {
    isReady: isDocumentReady,
    vendorName,
    vendorId,
    vendorField,
    purchaseOrderNumber,
    invoiceDate,
    dueDate,
    lineItemAmountSum,
    lineItems,
    availability,
  } = useDocumentInvoiceData()

  const createLineItemMatchOverlay = useOverlay()
  const searchPurchaseOrderOverlay = useOverlay()
  const removePOOverlay = useOverlay()
  const vendorUpdateOverlay = useOverlay()
  const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null)
  const [userAddedPOs, setUserAddedPOs] = useState(
    () => new Map<string, PDPurchaseOrder>(),
  )
  const [pendingMatchLines, setPendingMatchLines] = useState<MatchLineData[]>(
    [],
  )
  const [skipCreateConfirm, setSkipCreateConfirm] = useLocalStorage(
    'po-line-item-match-skip-create-confirm',
    false,
  )
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [poToRemove, setPoToRemove] = useState<{
    externalId: string
    connectionCount: number
  } | null>(null)

  // Data fetching
  const {
    poIds,
    purchaseOrders,
    vendorMap,
    reasonMap,
    poLineItemMatches,
    isLoadingPOs,
    isCreatingLineItemMatch,
    poLineItemMatchPerPoId,
    poLineItemMatchesQueryKey,
    deletePOLineItemMatches,
    createPOLineItemMatch,
  } = usePOMatchData({
    documentId: doc.id,
    ownerOrgId: doc.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    userAddedPOs,
  })

  // Other invoices matched to any PO this invoice has line item matches on.
  // Sorted so the same set of POs always produces the same cache key.
  const matchedPoIds = useMemo(
    () => [...poLineItemMatchPerPoId.keys()].toSorted(),
    [poLineItemMatchPerPoId],
  )
  const { relatedInvoices, isLoading: isLoadingRelatedInvoices } =
    useRelatedInvoices({
      documentId: doc.id,
      poIds: matchedPoIds,
    })

  // Which related invoice cards are expanded decides where their connectors
  // start: each tie row when expanded, the card's summary row when collapsed.
  const [expandedRelatedInvoiceIds, setExpandedRelatedInvoiceIds] = useState<
    Set<string>
  >(() => new Set())
  const toggleRelatedInvoiceExpanded = useCallback((invoiceId: string) => {
    setExpandedRelatedInvoiceIds((prev) => {
      const next = new Set(prev)
      if (next.has(invoiceId)) {
        next.delete(invoiceId)
      } else {
        next.add(invoiceId)
      }
      return next
    })
  }, [])
  const [showOtherInvoices, setShowOtherInvoices] = useLocalStorage(
    'po-view-show-other-invoices',
    true,
  )
  const [hoveredRelatedInvoiceId, setHoveredRelatedInvoiceId] = useState<
    string | null
  >(null)
  const handleShowOtherInvoicesChange = useCallback(
    (show: boolean) => {
      setShowOtherInvoices(show)
      if (!show) setHoveredRelatedInvoiceId(null)
    },
    [setShowOtherInvoices],
  )
  const relatedConnectorLines = useMemo(
    () =>
      buildRelatedConnectorLines(relatedInvoices, expandedRelatedInvoiceIds),
    [relatedInvoices, expandedRelatedInvoiceIds],
  )

  // Connector position tracking
  const {
    invoiceScrollRef,
    poScrollRef,
    connectorRef,
    invoiceContentRef,
    invoiceRowPositions,
    poRowPositions,
    relatedAnchorPositions,
    invoiceRowRefCallback,
    poRowRefCallback,
    relatedAnchorRefCallback,
    recalcPositions,
  } = useConnectorPositions()

  // Connector selection / highlight state
  const {
    highlightedInvoiceRowIds,
    highlightedPoRowIds,
    handleConnectorSelectionChange,
    handleRowHover,
    handleDiscrepancyHighlight,
  } = useConnectorSelection()

  // PO discrepancy check. The PO panel waits for the first result (so cards
  // never appear without discrepancies and then flip), but an error never
  // blocks it — the panel renders with the retry alert instead. The check
  // payload is built from this view's own data (invoice values, POs, line
  // matches), so it only runs once that data has loaded.
  const {
    discrepancies,
    isError: isDiscrepancyCheckError,
    isSuccess: hasDiscrepancyResult,
    refetch: refetchDiscrepancies,
  } = usePODiscrepancies({
    id: doc.id,
    purchaseOrderNumber,
    vendorId,
    lineItems,
    purchaseOrders,
    lineItemMatches: poLineItemMatches,
    enabled: !isLoadingPOs && isDocumentReady,
  })

  // Show the skeleton until the first discrepancy result lands (unless the
  // check failed — then show the POs alongside the retry alert).
  const waitingForDiscrepancies =
    !hasDiscrepancyResult && !isDiscrepancyCheckError

  // Clicking a cell with a discrepancy opens a popover with the breakdown,
  // anchored to that cell. While it's open, every row in the discrepancy's
  // match group is highlighted (both sides) and every cell in the group gets
  // the emphasized treatment.
  const [openDiscrepancy, setOpenDiscrepancy] = useState<{
    anchorEl: HTMLElement
    detail: CellDiscrepancy
  } | null>(null)

  const handleInvoiceCellClick = useCallback(
    (rowId: string, column: CellColumn, anchorEl: HTMLElement) => {
      const detail =
        discrepancies.invoiceRowCellDiscrepancies.get(rowId)?.[column]
      if (detail) setOpenDiscrepancy({ anchorEl, detail })
    },
    [discrepancies.invoiceRowCellDiscrepancies],
  )

  const handlePoCellClick = useCallback(
    (rowId: string, column: CellColumn, anchorEl: HTMLElement) => {
      const detail = discrepancies.poRowCellDiscrepancies.get(rowId)?.[column]
      if (detail) setOpenDiscrepancy({ anchorEl, detail })
    },
    [discrepancies.poRowCellDiscrepancies],
  )

  // Hovering a cell with a discrepancy previews the same group emphasis a click
  // locks in — every cell in the match group, both sides — without opening the
  // popover or pinning the row highlight.
  const [hoveredDiscrepancy, setHoveredDiscrepancy] =
    useState<CellDiscrepancy | null>(null)

  const handleInvoiceCellHover = useCallback(
    (rowId: string, column: CellColumn | null) => {
      setHoveredDiscrepancy(
        column
          ? discrepancies.invoiceRowCellDiscrepancies.get(rowId)?.[column] ??
              null
          : null,
      )
    },
    [discrepancies.invoiceRowCellDiscrepancies],
  )

  const handlePoCellHover = useCallback(
    (rowId: string, column: CellColumn | null) => {
      setHoveredDiscrepancy(
        column
          ? discrepancies.poRowCellDiscrepancies.get(rowId)?.[column] ?? null
          : null,
      )
    },
    [discrepancies.poRowCellDiscrepancies],
  )

  const { emphasizedInvoiceCells, emphasizedPoCells } = useMemo(() => {
    const emphasizedInvoiceCells = new Set<string>()
    const emphasizedPoCells = new Set<string>()
    const addGroup = ({ column, group }: CellDiscrepancy) => {
      group.invoiceRowIds.forEach((rowId) =>
        emphasizedInvoiceCells.add(emphasizedCellKey(rowId, column)),
      )
      group.poRowIds.forEach((rowId) =>
        emphasizedPoCells.add(emphasizedCellKey(rowId, column)),
      )
    }
    // Both the open popover and a hover emphasize their whole group.
    if (openDiscrepancy) addGroup(openDiscrepancy.detail)
    if (hoveredDiscrepancy) addGroup(hoveredDiscrepancy)
    return { emphasizedInvoiceCells, emphasizedPoCells }
  }, [openDiscrepancy, hoveredDiscrepancy])

  // Mirror the open discrepancy's match group into the shared row-highlight
  // state, so every row involved lights up (unioned with click/hover).
  useEffect(() => {
    handleDiscrepancyHighlight(openDiscrepancy?.detail.group ?? null)
  }, [openDiscrepancy, handleDiscrepancyHighlight])

  // Vendor-mismatch hover — hovering either side's vendor field discrepancy
  // emphasizes its counterpart(s) on the other side, like cells with a
  // discrepancy do.
  const [hoveredVendorDiscrepancy, setHoveredVendorDiscrepancy] = useState<
    { side: 'invoice' } | { side: 'po'; poId: string } | null
  >(null)

  // The invoice's vendor field is the counterpart of every PO vendor with a
  // discrepancy, so it emphasizes whenever any vendor discrepancy is
  // hovered. (FieldItem only applies emphasis when the field actually
  // carries a discrepancy.)
  const invoiceVendorEmphasized = !!hoveredVendorDiscrepancy
  const emphasizedVendorPoIds = useMemo(() => {
    const ids = new Set<string>()
    if (!hoveredVendorDiscrepancy) return ids
    if (hoveredVendorDiscrepancy.side === 'po') {
      ids.add(hoveredVendorDiscrepancy.poId)
    } else {
      discrepancies.poHeaderDiscrepancies.forEach(
        (headerDiscrepancies, poId) => {
          if (headerDiscrepancies.vendorDiscrepancy) ids.add(poId)
        },
      )
    }
    return ids
  }, [hoveredVendorDiscrepancy, discrepancies.poHeaderDiscrepancies])

  // Build match line data for ConnectorCanvas
  const matchLines: MatchLineData[] = useMemo(() => {
    const lines: MatchLineData[] = []

    purchaseOrders.forEach((po) => {
      const lineItemMatchesForPO =
        poLineItemMatchPerPoId.get(po.external_id) || []

      lineItemMatchesForPO.forEach((lim) => {
        const reasonNames = lim.match_reasons_ids
          .map((rid) => reasonMap.get(rid))
          .filter((n): n is string => !!n)

        lines.push({
          id: `match-${lim.id}`,
          sourceRowId: lim.document_row_id,
          targetRowId: lim.po_line_item_id,
          allReasons: reasonNames,
          aiReasoning: lim.ai_reasoning,
          poLineItemMatchId: lim.id,
          onDelete: () => {
            if (lineItemMatchesForPO.length === 1) {
              // Pin the PO in userAddedPOs so it stays visible in the panel.
              // Without this, deleting the last line item match would remove the PO
              // from matchedPoIds, causing it to disappear from the UI entirely.
              // We want to keep it visible so the user can see the PO and re-match to it if desired.
              setUserAddedPOs(
                (prev) => new Map([...prev, [po.external_id, po]]),
              )
            }
          },
        })
      })
    })

    return lines
  }, [purchaseOrders, poLineItemMatchPerPoId, reasonMap])

  // Clear pending lines once server data includes them
  useEffect(() => {
    if (!pendingMatchLines.length) return
    const serverKeys = new Set(
      matchLines.map((m) => `${m.sourceRowId}-${m.targetRowId}`),
    )
    const remaining = pendingMatchLines.filter(
      (p) => !serverKeys.has(`${p.sourceRowId}-${p.targetRowId}`),
    )
    if (remaining.length < pendingMatchLines.length) {
      setPendingMatchLines(remaining)
    }
  }, [matchLines, pendingMatchLines])

  // Merge server matches with pending (in-flight) matches
  const allMatchLines = useMemo(
    () => [...matchLines, ...pendingMatchLines],
    [matchLines, pendingMatchLines],
  )

  const connectedInvoiceRowIds = useMemo(
    () => new Set(allMatchLines.map((m) => m.sourceRowId)),
    [allMatchLines],
  )

  const connectedPoRowIds = useMemo(
    () => new Set(allMatchLines.map((m) => m.targetRowId)),
    [allMatchLines],
  )

  // Every row (either side) carrying at least one cell discrepancy — used to
  // color a connector yellow when either of its endpoints has a discrepancy.
  const discrepancyRowIds = useMemo(() => {
    const ids = new Set<string>()
    discrepancies.invoiceRowCellDiscrepancies.forEach((_, rowId) =>
      ids.add(rowId),
    )
    discrepancies.poRowCellDiscrepancies.forEach((_, rowId) => ids.add(rowId))
    return ids
  }, [
    discrepancies.invoiceRowCellDiscrepancies,
    discrepancies.poRowCellDiscrepancies,
  ])

  // Invoice rows with no PO match — derived client-side from the match lines
  // (not the discrepancy response): a missing match is obvious locally.
  // Empty while POs/matches are still loading so rows don't all flash dim.
  const unmatchedInvoiceRowIds = useMemo(() => {
    const ids = new Set<string>()
    if (isLoadingPOs) return ids
    lineItems.forEach((li) => {
      if (!connectedInvoiceRowIds.has(li.rowId)) ids.add(li.rowId)
    })
    return ids
  }, [isLoadingPOs, lineItems, connectedInvoiceRowIds])

  // PO line item external ID → PO external ID mapping (for drag-to-connect)
  const poRowIdToPoId = useMemo(() => {
    const map = new Map<string, string>()
    purchaseOrders.forEach((po) => {
      po.line_items.forEach((li) => {
        map.set(li.external_id, po.external_id)
      })
    })
    return map
  }, [purchaseOrders])

  // Create a line item match directly
  const createLineItemMatch = useCallback(
    async (invoiceRowId: string, poLineItemId: string, poId: string) => {
      if (!doc.id) return

      createPOLineItemMatch({
        id: generateUuid(),
        po_id: poId,
        po_line_item_id: poLineItemId,
        document_row_id: invoiceRowId,
        match_reasons_ids: [USER_MATCHED_REASON_ID],
      } as POLineItemMatch)
    },
    [createPOLineItemMatch, doc.id],
  )

  // Drag-to-connect handler
  const handleDragConnect = useCallback(
    async (invoiceRowId: string, poRowId: string) => {
      const poId = poRowIdToPoId.get(poRowId)
      if (!poId) return

      if (skipCreateConfirm) {
        await createLineItemMatch(invoiceRowId, poRowId, poId)
        return
      }

      setPendingMatch({ invoiceRowId, poLineItemId: poRowId, poId })
      setDoNotShowAgain(false)
      createLineItemMatchOverlay.open()
    },
    [
      createLineItemMatchOverlay,
      createLineItemMatch,
      poRowIdToPoId,
      skipCreateConfirm,
    ],
  )

  const handleCreateConfirmClose = useCallback(() => {
    createLineItemMatchOverlay.close()
    setPendingMatch(null)
    setDoNotShowAgain(false)
  }, [createLineItemMatchOverlay])

  const handleConfirmCreate = useCallback(async () => {
    if (!pendingMatch) return

    if (doNotShowAgain) {
      setSkipCreateConfirm(true)
    }

    await createLineItemMatch(
      pendingMatch.invoiceRowId,
      pendingMatch.poLineItemId,
      pendingMatch.poId,
    )
    handleCreateConfirmClose()
  }, [
    createLineItemMatch,
    doNotShowAgain,
    handleCreateConfirmClose,
    pendingMatch,
    setSkipCreateConfirm,
  ])

  // Every PO the user has closed. Tracked separately from the auto-pin hook's
  // own bookkeeping, which only covers POs it pinned itself — a PO that was in
  // the panel because it had line matches would otherwise get re-pinned by
  // number the moment removing it deleted those matches.
  const [dismissedPoIds, setDismissedPoIds] = useState<Set<string>>(
    () => new Set(),
  )

  const removeUserAddedPo = useCallback((poId: string) => {
    setUserAddedPOs((prev) => {
      const next = new Map(prev)
      next.delete(poId)
      return next
    })
    setDismissedPoIds((prev) => new Set(prev).add(poId))
  }, [])

  const handleAddPurchaseOrder = useCallback(
    (purchaseOrder: PDPurchaseOrder) => {
      setUserAddedPOs((prev) => {
        const next = new Map(prev)
        next.set(purchaseOrder.external_id, purchaseOrder)
        return next
      })
    },
    [],
  )

  const { isLoadingMatchingPO } = useMatchingPONumberPO({
    invoicePoNumber: purchaseOrderNumber,
    orgId: doc.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    purchaseOrders,
    isLoadingPOs,
    dismissedPoIds,
    onAddPurchaseOrder: handleAddPurchaseOrder,
  })

  const handleRemovePO = useCallback(
    (poExternalId: string) => {
      const lineItemMatches = poLineItemMatchPerPoId.get(poExternalId) ?? []

      if (lineItemMatches.length === 0) {
        // No connections — just remove from local state
        removeUserAddedPo(poExternalId)
      } else {
        // Has connections — show confirmation
        setPoToRemove({
          externalId: poExternalId,
          connectionCount: lineItemMatches.length,
        })
        removePOOverlay.open()
      }
    },
    [poLineItemMatchPerPoId, removeUserAddedPo, removePOOverlay],
  )

  const handleConfirmRemovePO = useCallback(() => {
    if (!poToRemove) return
    // Delete all line item matches for this PO
    const matches = poLineItemMatchPerPoId.get(poToRemove.externalId) ?? []
    deletePOLineItemMatches(matches.map((m) => m.id))
    removeUserAddedPo(poToRemove.externalId)
    setPoToRemove(null)
    removePOOverlay.close()
  }, [
    deletePOLineItemMatches,
    poLineItemMatchPerPoId,
    poToRemove,
    removeUserAddedPo,
    removePOOverlay,
  ])

  const {
    sharedInvoiceJobNumber,
    sharedInvoicePhaseNumber,
    showHeaderJobNumber,
    showHeaderPhaseNumber,
  } = useMemo(() => {
    const sharedInvoiceJobNumber = getSharedLineItemJob(lineItems)
    const sharedInvoicePhaseNumber = getSharedLineItemPhase(lineItems)

    // Only ever fold Job/Phase into the header when the project actually has
    // that field configured — otherwise it's hidden entirely (no header, no column).
    let showHeaderJobNumber =
      availability.job && sharedInvoiceJobNumber !== undefined
    let showHeaderPhaseNumber =
      availability.phase && sharedInvoicePhaseNumber !== undefined

    purchaseOrders.forEach((po) => {
      if (!showHeaderJobNumber && !showHeaderPhaseNumber) return

      const poLineItems = po.line_items.map((li) => ({
        jobNumber: li.job_num,
        phaseNumber: li.phase_num,
      }))

      if (
        showHeaderJobNumber &&
        getSharedLineItemJob(poLineItems) === undefined
      ) {
        showHeaderJobNumber = false
      }

      if (
        showHeaderPhaseNumber &&
        getSharedLineItemPhase(poLineItems) === undefined
      ) {
        showHeaderPhaseNumber = false
      }
    })

    return {
      sharedInvoiceJobNumber,
      sharedInvoicePhaseNumber,
      showHeaderJobNumber,
      showHeaderPhaseNumber,
    }
  }, [lineItems, purchaseOrders, availability.job, availability.phase])

  // Column visibility for both the invoice-side and PO-side line-item tables —
  // Job/Phase additionally fold into the header when possible; the rest is a
  // direct mirror of which fields the project has configured.
  const columnVisibility: LineItemColumnVisibility = useMemo(
    () => ({
      description: availability.description,
      job: availability.job && !showHeaderJobNumber,
      phase: availability.phase && !showHeaderPhaseNumber,
      quantity: availability.quantity,
      rate: availability.rate,
      amount: availability.amount,
    }),
    [availability, showHeaderJobNumber, showHeaderPhaseNumber],
  )

  // POs whose number matches the invoice's PO number — surfaced first in the
  // panel and highlighted, even without a line item match. Derived from the
  // unordered list so the display order can be built from it.
  const poNumberMatchIds = useMemo(() => {
    const ids = new Set<string>()
    purchaseOrders.forEach((po) => {
      if (poNumbersMatch(po.number, purchaseOrderNumber)) {
        ids.add(po.external_id)
      }
    })
    return ids
  }, [purchaseOrders, purchaseOrderNumber])

  const displayPurchaseOrders = usePODisplayOrder(
    purchaseOrders,
    lineItems,
    poLineItemMatchPerPoId,
    isLoadingPOs,
    poNumberMatchIds,
  )

  const suggestedVendor = useMemo(() => {
    const firstDifferentVendorPO = displayPurchaseOrders.find(
      (po) => po.vendor_id && po.vendor_id !== vendorId,
    )
    if (!firstDifferentVendorPO || !firstDifferentVendorPO.vendor_id)
      return null

    // Only suggest if mismatch
    if (firstDifferentVendorPO.vendor_id === vendorId) return null

    return vendorMap.get(firstDifferentVendorPO.vendor_id)
  }, [displayPurchaseOrders, vendorId, vendorMap])

  // The invoice card renders outside the PO panel's skeleton gate, so without
  // this its chip would flash a PO# mismatch warning that disappears once the
  // auto-pin lookup turns up a number-matching PO.
  const poMatchDataSettled =
    !isLoadingPOs && !waitingForDiscrepancies && !isLoadingMatchingPO

  // The other invoices load alongside, but only show once the focused invoice
  // and its POs have, and once their own cards have everything to show, so
  // they never appear before the invoice they're about or fill in piecemeal.
  const showRelatedInvoices =
    isDocumentReady && poMatchDataSettled && !isLoadingRelatedInvoices

  // Recalc positions when data changes. Discrepancies, the unmatched set, and
  // poMatchDataSettled are included because they can change card layout above
  // the tables (e.g. the PO number mismatch strip, the vendor field's
  // discrepancy, or the "N lines unmatched" text wrapping), which shifts every
  // row — and the connector dots with them.
  useEffect(() => {
    const timer = setTimeout(recalcPositions, 0)
    return () => clearTimeout(timer)
  }, [
    recalcPositions,
    lineItems,
    displayPurchaseOrders,
    discrepancies,
    unmatchedInvoiceRowIds,
    relatedConnectorLines,
    poMatchDataSettled,
    showOtherInvoices,
  ])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.default',
        backgroundImage:
          'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
    >
      {isDiscrepancyCheckError && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 2 }}
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              variant="text"
              onClick={() => refetchDiscrepancies()}
              startIcon={<Sync />}
            >
              Retry
            </Button>
          }
        >
          Discrepancies couldn&apos;t be checked. Please refresh the page or
          click Retry to try again.
        </Alert>
      )}

      {/* Main content: side-by-side layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Invoice panel (left) */}
        <Box
          ref={invoiceScrollRef}
          sx={{
            width: INVOICE_PANE_WIDTH,
            overflow: 'auto',
            overscrollBehavior: 'none',
            p: 2,
          }}
        >
          <Box ref={invoiceContentRef}>
            <DataGroupNode
              title="Invoice"
              icon={ReceiptLongIcon}
              amountTotal={availability.amount ? lineItemAmountSum : undefined}
              poNumber={purchaseOrderNumber}
              poNumberDiscrepancy={
                poMatchDataSettled && poNumberMatchIds.size === 0
                  ? discrepancies.invoiceDiscrepancies.poNumberDiscrepancy
                  : undefined
              }
            >
              <Stack
                direction="row"
                spacing={3}
                sx={{ mb: 2 }}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
              >
                {availability.vendor && (
                  <FieldItem
                    label="Vendor"
                    value={vendorName}
                    discrepancy={
                      discrepancies.invoiceDiscrepancies.vendorDiscrepancy
                    }
                    discrepancyEmphasized={invoiceVendorEmphasized}
                    onDiscrepancyHoverChange={(hovering) =>
                      setHoveredVendorDiscrepancy(
                        hovering ? { side: 'invoice' } : null,
                      )
                    }
                    action={
                      suggestedVendor &&
                      vendorField && (
                        <Tooltip title="Update Vendor">
                          <IconButton
                            aria-label="Update vendor"
                            color="primary"
                            size="small"
                            onClick={vendorUpdateOverlay.open}
                          >
                            <CompareArrows fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  />
                )}
                {availability.invoiceDate && (
                  <FieldItem label="Invoice Date" value={invoiceDate} />
                )}
                {availability.dueDate && (
                  <FieldItem label="Due Date" value={dueDate} />
                )}
                {showHeaderJobNumber && (
                  <FieldItem
                    label="Job"
                    value={sharedInvoiceJobNumber || null}
                    tooltip={lineItems[0]?.jobDisplayName}
                  />
                )}
                {showHeaderPhaseNumber && (
                  <FieldItem
                    label="Phase"
                    value={sharedInvoicePhaseNumber || null}
                    tooltip={lineItems[0]?.phaseDisplayName}
                  />
                )}
                {unmatchedInvoiceRowIds.size > 0 && (
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="warning.main"
                    sx={{ ml: 'auto', alignSelf: 'flex-end' }}
                  >
                    {unmatchedInvoiceRowIds.size}{' '}
                    {unmatchedInvoiceRowIds.size === 1 ? 'line' : 'lines'}{' '}
                    unmatched
                  </Typography>
                )}
              </Stack>

              <Divider />

              <LineItemsTable
                rows={lineItems}
                rowRefCallback={invoiceRowRefCallback}
                highlightedRowIds={highlightedInvoiceRowIds}
                columnVisibility={columnVisibility}
                cellDiscrepancies={discrepancies.invoiceRowCellDiscrepancies}
                dimmedRowIds={unmatchedInvoiceRowIds}
                emphasizedCells={emphasizedInvoiceCells}
                onRowHover={(rowId) =>
                  handleRowHover('invoice', rowId, allMatchLines)
                }
                onDiscrepancyCellClick={handleInvoiceCellClick}
                onDiscrepancyCellHover={handleInvoiceCellHover}
              />
            </DataGroupNode>

            {showRelatedInvoices && (
              <RelatedInvoicesList
                invoices={relatedInvoices}
                purchaseOrders={purchaseOrders}
                availability={availability}
                expandedInvoiceIds={expandedRelatedInvoiceIds}
                onToggleExpanded={toggleRelatedInvoiceExpanded}
                showInvoices={showOtherInvoices}
                onShowInvoicesChange={handleShowOtherInvoicesChange}
                anchorRefCallback={relatedAnchorRefCallback}
                onHoveredInvoiceChange={setHoveredRelatedInvoiceId}
              />
            )}
          </Box>
        </Box>

        {/* Connector column (middle) */}
        <Box
          ref={connectorRef}
          sx={{
            width: CONNECTOR_PANE_WIDTH,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <ConnectorCanvas
            matches={allMatchLines}
            invoiceRowPositions={invoiceRowPositions}
            poRowPositions={poRowPositions}
            connectedInvoiceRowIds={connectedInvoiceRowIds}
            connectedPoRowIds={connectedPoRowIds}
            discrepancyRowIds={discrepancyRowIds}
            poLineItemMatchesQueryKey={poLineItemMatchesQueryKey}
            relatedLines={
              showRelatedInvoices && showOtherInvoices
                ? relatedConnectorLines
                : []
            }
            relatedAnchorPositions={relatedAnchorPositions}
            highlightedRelatedInvoiceId={hoveredRelatedInvoiceId}
            onConnect={handleDragConnect}
            onSelectionChange={handleConnectorSelectionChange}
          />
        </Box>

        {/* Purchase Orders panel (right) */}
        <Box
          ref={poScrollRef}
          sx={{
            width: PURCHASE_ORDER_PANE_WIDTH,
            overflow: 'auto',
            overscrollBehavior: 'none',
            p: 2,
          }}
        >
          {isLoadingPOs || waitingForDiscrepancies || isLoadingMatchingPO ? (
            <SkeletonPOCard />
          ) : purchaseOrders.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                textAlign: 'center',
                gap: 1.5,
              }}
            >
              <ShoppingCartIcon
                sx={{ fontSize: 56, color: 'text.disabled', mb: 0.5 }}
              />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                No purchase order matches
              </Typography>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ maxWidth: 280 }}
              >
                No purchase orders have been matched to this invoice yet. Add
                one to get started.
              </Typography>
              <Button
                startIcon={<Add />}
                color="green"
                onClick={searchPurchaseOrderOverlay.open}
                sx={{ mt: 1 }}
              >
                Add Purchase Order
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {displayPurchaseOrders.map((po) => {
                const hasConnections = po.line_items.some((li) =>
                  connectedPoRowIds.has(li.external_id),
                )
                const hasDiscrepancy = discrepancies.poIdsWithDiscrepancies.has(
                  po.external_id,
                )
                const poHeaderDiscrepancies =
                  discrepancies.poHeaderDiscrepancies.get(po.external_id)
                const headerColor = hasDiscrepancy
                  ? 'warning.main'
                  : hasConnections
                  ? green[500]
                  : 'grey.500'

                return (
                  <DataGroupNode
                    key={po.external_id}
                    title="Purchase Order"
                    icon={ShoppingCartIcon}
                    poNumber={po.number}
                    headerColor={headerColor}
                    onClose={() => handleRemovePO(po.external_id)}
                    amountTotal={po.amount}
                    poNumberDiscrepancy={
                      poHeaderDiscrepancies?.poNumberDiscrepancy
                    }
                  >
                    <PurchaseOrderData
                      vendorName={
                        po.vendor_id
                          ? vendorMap.get(po.vendor_id)?.pd_display_name || null
                          : null
                      }
                      showHeaderJobNumber={showHeaderJobNumber}
                      showHeaderPhaseNumber={showHeaderPhaseNumber}
                      columnVisibility={columnVisibility}
                      date={po.posted_on}
                      lineItems={po.line_items.map((li) => ({
                        rowId: li.external_id,
                        description: li.description,
                        jobNumber: li.job_num || null,
                        phaseNumber: li.phase_num || null,
                        quantity: li.quantity,
                        remainingQuantity: li.remaining_quantity,
                        rate: li.unit_price,
                        amount: li.amount,
                        isLumpSum: isLumpSumPoLine(li.quantity, li.amount),
                        remainingAmount: li.remaining_amount,
                      }))}
                      rowRefCallback={poRowRefCallback}
                      highlightedRowIds={highlightedPoRowIds}
                      vendorDiscrepancy={
                        poHeaderDiscrepancies?.vendorDiscrepancy
                      }
                      vendorDiscrepancyEmphasized={emphasizedVendorPoIds.has(
                        po.external_id,
                      )}
                      onVendorDiscrepancyHover={(hovering) =>
                        setHoveredVendorDiscrepancy(
                          hovering
                            ? { side: 'po', poId: po.external_id }
                            : null,
                        )
                      }
                      poNumberMatch={poNumberMatchIds.has(po.external_id)}
                      cellDiscrepancies={discrepancies.poRowCellDiscrepancies}
                      emphasizedCells={emphasizedPoCells}
                      onRowHover={(rowId) =>
                        handleRowHover('po', rowId, allMatchLines)
                      }
                      onDiscrepancyCellClick={handlePoCellClick}
                      onDiscrepancyCellHover={handlePoCellHover}
                    />
                  </DataGroupNode>
                )
              })}
            </Stack>
          )}
          {purchaseOrders.length > 0 && (
            <Button
              color="green"
              variant="text"
              startIcon={<Add />}
              onClick={searchPurchaseOrderOverlay.open}
              sx={{ mt: 2, ml: 'auto', display: 'flex' }}
            >
              Add Purchase Order
            </Button>
          )}
        </Box>
      </Box>

      <DiscrepancyPopover
        open={!!openDiscrepancy}
        anchorEl={openDiscrepancy?.anchorEl ?? null}
        detail={openDiscrepancy?.detail ?? null}
        onClose={() => setOpenDiscrepancy(null)}
      />

      <CreateMatchConfirmDialog
        overlay={createLineItemMatchOverlay}
        doNotShowAgain={doNotShowAgain}
        isCreating={isCreatingLineItemMatch}
        onClose={handleCreateConfirmClose}
        onDoNotShowAgainChange={setDoNotShowAgain}
        onConfirm={handleConfirmCreate}
      />

      <AddPurchaseOrderDialog
        overlay={searchPurchaseOrderOverlay}
        orgId={doc.owner_org_id}
        orgCredentialTypeId={project.org_credential_type_id || ''}
        defaultVendorId={vendorId}
        addedPoIds={poIds}
        onAddPurchaseOrder={handleAddPurchaseOrder}
      />
      {suggestedVendor && (
        <UpdateVendorPopover
          overlay={vendorUpdateOverlay}
          suggestedVendor={suggestedVendor}
          vendorData={{
            value: vendorName,
            field: vendorField as ProjectGridField,
          }}
        />
      )}

      <Dialog
        {...removePOOverlay}
        title="Remove Purchase Order"
        onClose={() => {
          setPoToRemove(null)
          removePOOverlay.close()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>
            This purchase order has{' '}
            <strong>
              {poToRemove?.connectionCount} line item{' '}
              {poToRemove?.connectionCount === 1 ? 'match' : 'matches'}
            </strong>
            . Removing it will delete all connections to this purchase order.
          </Typography>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="text"
            onClick={() => {
              setPoToRemove(null)
              removePOOverlay.close()
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemovePO}
          >
            Remove
          </Button>
        </DialogFooter>
      </Dialog>
    </Box>
  )
}
